import { SubjectType } from '@src/app/generated/globalTypes';
import { Id } from '@ess/jg-rule-executor';
import { ToJsonInputTranslatable } from '@src/app/model/to-json-input-translatable';

/**
 * Represents the data object used to configure and display a Typed Form modal dialog. See {@link TypedFormModal}.
 */
export class TypedFormModalDataObject {
  /** The name of the modal.  */
  name: string;

  /** SubjectType of the input to establish which documentation to retrieve and which mutation to call on submit. */
  inputType: SubjectType;

  /** Input of a type extending ToJsonInputTranslatable, provided to the class. */
  input: ToJsonInputTranslatable | ToJsonInputTranslatable[];

  /** The id to show in the form, provided to the class. */
  id: Id<any>;

  /** The optional text to be displayed in the header of the modal. */
  headerText?: string;

  /** The optional text to be displayed on the submit button of the modal. */
  submitText?: string;

  /** An optional title for the subject of the modal. */
  subjectTitle?: string;

  /** An optional array of field names to be skipped in the modal. */
  skipFields?: string[] = [];

  /** An optional flag indicating whether the modal is read-only. */
  readOnly?: boolean;

  /** An optional flag indicating whether data we're working with is scoped. Will show scope selection if true. */
  isScoped?: boolean;

  /**
   * Constructs a new {@link TypedFormModalDataObject}.
   *
   * @param name - The name of the modal.
   * @param subjectType - SubjectType to predefine which mutation to call on submit.
   * @param input - Input of a type extending ToJsonInputTranslatable, provided to the class.
   * @param id - The id to show in the form, provided to the class.
   * @param skipFields - An optional array of field names to be skipped in the modal.
   * @param headerText - The text to be displayed in the header of the modal.
   * @param subjectTitle - The text to be displayed in the subtitle below the header
   * @param submitText - The text to be displayed on the submit button of the modal.
   * @param readOnly - An optional flag indicating whether the modal is read-only.
   * @param isScoped - An optional flag indicating whether the modal is scoped.
   */
  constructor(
    name: string,
    subjectType: SubjectType,
    input: ToJsonInputTranslatable | ToJsonInputTranslatable[],
    id: Id<any>,
    skipFields: string[] = [],
    headerText: string = 'formModal.editTitle',
    subjectTitle: string = 'formModal.idTitle',
    submitText: string = 'buttons.submit',
    readOnly?: boolean,
    isScoped?: boolean,
  ) {
    this.name = name;
    this.inputType = subjectType;
    this.input = input;
    this.id = id;
    this.headerText = headerText;
    this.submitText = submitText;
    this.skipFields = skipFields;
    this.subjectTitle = subjectTitle;
    this.readOnly = readOnly;
    this.isScoped = isScoped;
  }
}
